export interface OpenAISettings {
    apiKey: string;
    saveLocally: boolean;
    useProxy: boolean;
    model: string;
    voice: string;
    speed: number;
    format: string;
}

export interface ElevenLabsSettings {
    apiKey: string;
    saveLocally: boolean;
    useProxy: boolean;
    voiceId: string;
    modelId: string;
    stability: number;
    similarity: number;
    speed: number;
}

export interface DeepgramSettings {
    apiKey: string;
    saveLocally: boolean;
    useProxy: boolean;
    model: string;
    encoding?: string;
    sampleRate?: number;
    speed: number;
}

export interface PiperSettings {
    voiceId: string | null; // e.g., 'en_US-amy-medium'
    speed: number;
    pitch: number; // Piper doesn't always support pitch well, but we can try
}

export interface VoiceSettings {
    provider: 'free' | 'openai' | 'elevenlabs' | 'deepgram' | 'piper';
    free: {
        voiceURI: string | null;
        rate: number;
        pitch: number;
    };
    openai: OpenAISettings;
    elevenlabs: ElevenLabsSettings;
    deepgram: DeepgramSettings;
    piper: PiperSettings;
    cacheAudio: boolean;
    cacheQuotaMB: number;
    usePrerecordedAudio?: boolean;
    autoSpeakChat: boolean;
    autoPageFlip?: boolean;
}

const STORAGE_KEY = 'verseflip.voiceSettings.v1';

const DEFAULT_SETTINGS: VoiceSettings = {
    provider: 'free',
    free: {
        voiceURI: null,
        rate: 1.0,
        pitch: 1.0,
    },
    openai: {
        apiKey: '',
        saveLocally: false,
        useProxy: true,
        model: 'tts-1',
        voice: 'alloy',
        speed: 1.0,
        format: 'mp3'
    },
    elevenlabs: {
        apiKey: '',
        saveLocally: false,
        useProxy: true,
        voiceId: '',
        modelId: 'eleven_monolingual_v1',
        stability: 0.5,
        similarity: 0.75,
        speed: 1.0
    },
    deepgram: {
        apiKey: '',
        saveLocally: false,
        useProxy: true,
        model: 'aura-asteria-en',
        speed: 1.0
    },
    piper: {
        voiceId: null,
        speed: 1.0,
        pitch: 1.0
    },
    cacheAudio: false,
    cacheQuotaMB: 500, // Default 500MB
    usePrerecordedAudio: true,
    autoSpeakChat: true,
    autoPageFlip: false
};

export function loadSettings(): VoiceSettings {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            const merged = { ...DEFAULT_SETTINGS, ...parsed };
            // Deep sanitize speeds to prevent crashing on load if corrupt data exists
            if (merged.openai) merged.openai.speed = ensureFinite(merged.openai.speed);
            if (merged.elevenlabs) merged.elevenlabs.speed = ensureFinite(merged.elevenlabs.speed);
            if (merged.deepgram) merged.deepgram.speed = ensureFinite(merged.deepgram.speed);
            if (merged.piper) merged.piper.speed = ensureFinite(merged.piper.speed);

            // MIGRATION: Deepgram 'voice' -> 'model'
            if (parsed.deepgram && parsed.deepgram.voice) {
                const legacyVoice = parsed.deepgram.voice;
                if (legacyVoice.startsWith('aura-')) {
                    merged.deepgram.model = legacyVoice;
                }
                delete merged.deepgram.voice;
            }

            if (merged.deepgram.model && merged.deepgram.model.includes('asteria')) {
                merged.deepgram.model = 'aura-2-athena-en';
            }

            return merged;
        }
    } catch (e) {
        console.error('Failed to load voice settings', e);
    }
    return DEFAULT_SETTINGS;
}

function ensureFinite(val: any, def: number = 1.0): number {
    const n = Number(val);
    return Number.isFinite(n) ? n : def;
}

export function saveSettings(settings: VoiceSettings) {
    const toSave = { ...settings };

    // Security: Don't persist keys unless explicitly requested
    if (!toSave.openai.saveLocally) toSave.openai.apiKey = '';
    if (!toSave.elevenlabs.saveLocally) toSave.elevenlabs.apiKey = '';
    if (!toSave.deepgram.saveLocally) toSave.deepgram.apiKey = '';

    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
}

// Helper to pick best default voice
export function pickDefaultVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
    if (voices.length === 0) return null;

    const exact = voices.find(v => v.name === 'Google UK English Male');
    if (exact) return exact;

    const googleGB = voices.find(v =>
        v.name.includes('Google') &&
        (v.lang === 'en-GB' || v.name.includes('UK') || v.name.includes('United Kingdom'))
    );
    if (googleGB) return googleGB;

    const natural = voices.find(v => v.name.includes('Online Natural') || v.name.includes('Natural'));
    if (natural) return natural;

    const googleAny = voices.find(v => v.name.includes('Google') && v.lang.startsWith('en'));
    if (googleAny) return googleAny;

    const anyGB = voices.find(v => v.lang === 'en-GB');
    if (anyGB) return anyGB;

    const anyUS = voices.find(v => v.lang === 'en-US');
    if (anyUS) return anyUS;

    return voices[0];
}
